import axios from "axios";
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  Divider,
  FormControl,
  Radio,
  TextField,
  Typography,
} from "@mui/material";
import localize from "../../../common/localize";
import ModalBackground from "../../../components/common/ModalBackground";
import { MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION } from "../../../common/utils";
import { useEffect, useState } from "react";
import {
  AdditionalStatusCodeEnum,
  getAdditionalCostStatusName,
} from "../../../common/EntitiesManagement";
import { getInputLabelProps, getInputProps } from "../../../common/appStyles";
import { SUPPLY_USAGE_API } from "../../../services/config";
import useGlobalContext from "../../../hooks/useGlobalContext";

export default function SupplyRequestStatusChange({
  eventId,
  supplyUsageModel,
  show,
  setShow,
  onResult,
}) {
  const { config } = useGlobalContext();
  const maximumCharactersForSupplyUsageStatusObservation =
    MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION;

  const [
    supplyUsageStatusObservationCount,
    setSupplyUsageStatusObservationCount,
  ] = useState(0);

  const [supplyUsageQuantity, setSupplyUsageQuantity] = useState(
    supplyUsageModel?.SupplyData?.Quantity <= supplyUsageModel?.Quantity
      ? supplyUsageModel?.SupplyData?.Quantity
      : supplyUsageModel?.Quantity ?? 1
  );
  const [supplyUsage, setSupplyUsage] = useState({ ...supplyUsageModel });

  useEffect(() => {
    if (supplyUsage?.StatusObservation) {
      setSupplyUsageStatusObservationCount(
        supplyUsage?.StatusObservation.length
      );
    } else {
      setSupplyUsageStatusObservationCount(0);
    }
  }, [supplyUsage?.StatusObservation]);

  const handleSupplyUsageStatusObservationChange = (e) => {
    setSupplyUsage({
      ...supplyUsage,
      StatusObservation: e.target.value,
    });
  };

  const handleSupplyUsageStatusChange = (e) => {
    if (!e) {
      setSupplyUsage({
        ...supplyUsage,
        SelectedStatus: {
          Id: AdditionalStatusCodeEnum.InProgress,
          Name: getAdditionalCostStatusName(
            AdditionalStatusCodeEnum.InProgress
          ),
        },
        Status: AdditionalStatusCodeEnum.InProgress,
      });
      return;
    }

    setSupplyUsage({
      ...supplyUsage,
      SelectedStatus: {
        Id: e.Id,
        Name: getAdditionalCostStatusName(e.Id),
      },
      Status: e.Id,
    });
  };

  const UpdateSupplyUsageStatus = async (usage) => {
    try {
      const modelToSend = {
        ...usage,
        EventId: eventId,
      };
      const data = await axios.put(
        `${SUPPLY_USAGE_API}/${usage.Id}`,
        modelToSend,
        config
      );
      validateOperationResultRaw(data?.data);
    } catch (error) {
      validateOperationResult(error.response.data);
    }
  };

  const validateOperationResultRaw = (result) => {
    if (result?.Ok) {
      onResult && onResult({ result: true });
      return;
    }

    if (!result.Ok) {
      onResult &&
        onResult({
          result: false,
          data: JSON.stringify(result?.data || result?.Data),
        });
    }
  };

  const validateOperationResult = (result) => {
    if (result.ok || result.Ok) {
      onResult && onResult({ result: true });
      return;
    }

    if (!result.ok && !result.Ok) {
      onResult &&
        onResult({
          result: false,
          data: JSON.stringify(result?.data || result?.Data || result?.error),
        });
    }
  };

  const assingQuantity = (e) => {
    if (e?.target?.value) {
      const value = Math.max(e?.target?.value, 0);

      setSupplyUsageQuantity(
        Math.max(0, Math.min(supplyUsage?.SupplyData?.Quantity, value))
      );
      return;
    }

    setSupplyUsageQuantity(supplyUsage?.SupplyData?.Quantity <= 0 ? 0 : 1);
  };

  return (
    <ModalBackground
      validators={
        supplyUsage?.Status === AdditionalStatusCodeEnum.InProgress ||
        (supplyUsage?.Status === AdditionalStatusCodeEnum.Approved &&
          supplyUsageQuantity <= 0) ||
        (supplyUsage?.Status === AdditionalStatusCodeEnum.Rejected &&
          supplyUsageStatusObservationCount === 0)
      }
      isDrawerMode
      showModal={show}
      setShowModal={setShow}
      title={localize.supply_usage_status_change_title}
      onClickToCancel={() => {
        onResult && onResult({ result: false });
      }}
      onClickToSubmit={() => {
        UpdateSupplyUsageStatus({
          ...supplyUsage,
          Quantity: supplyUsageQuantity,
        });
      }}
      Component={
        <Box className="w-full flex flex-col gap-2 my-2">
          <Box className="flex w-full justify-between">
            <Box className="two_columns_grid">
              <label className="field_title_text">
                <b>{`${localize.code}:`}</b>
              </label>
              <label className="field_title_text">
                {supplyUsage?.SupplyData?.Code}
              </label>
              <label className="field_title_text">
                <b>{`${localize.name}:`}</b>
              </label>
              <label className="field_title_text">
                {supplyUsage?.SupplyData?.Name}
              </label>
              <label className="field_title_text">
                <b>{`${localize.request}:`}</b>
              </label>
              <label className="field_title_text">
                {`${supplyUsage?.Quantity} ${
                  supplyUsage?.SupplyData?.Unit?.Name ?? ""
                } ${
                  supplyUsage?.SupplyData?.Unit?.Symbol
                    ? `(${supplyUsage?.SupplyData?.Unit?.Symbol})`
                    : ""
                }`}
              </label>
            </Box>
          </Box>

          {supplyUsage?.SupplyData?.Quantity <= 0 && (
            <Alert severity="error">
              <label className="text-xs">
                {localize.there_is_no_supplies_on_warehouse}
              </label>
            </Alert>
          )}

          {supplyUsage?.SupplyData?.Quantity > 0 &&
            supplyUsage?.SupplyData?.Quantity < supplyUsage?.Quantity && (
              <Alert severity="warning">
                <label className="text-xs">
                  {localize.not_enough_quantity.replace(
                    "%s",
                    supplyUsage?.SupplyData?.Quantity
                  )}
                </label>
              </Alert>
            )}

          <Divider />

          <Box className="flex flex-col gap-4 mt-2">
            <Box sx={{ display: "flex", gap: 2 }}>
              <Card
                variant="outlined"
                onClick={() =>
                  handleSupplyUsageStatusChange({
                    Id: AdditionalStatusCodeEnum.Approved,
                  })
                }
                sx={{
                  flex: 1,
                  border:
                    supplyUsage?.Status === AdditionalStatusCodeEnum.Approved
                      ? "2px solid #2E7D32"
                      : "1px solid #E0E0E0",
                  backgroundColor:
                    supplyUsage?.Status === AdditionalStatusCodeEnum.Approved
                      ? "#E8F5E9"
                      : "transparent",
                  cursor: "pointer",
                  borderRadius: "8px",
                }}
              >
                <CardActionArea
                  sx={{
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <Radio
                    size="small"
                    checked={
                      supplyUsage?.Status === AdditionalStatusCodeEnum.Approved
                    }
                    sx={{
                      color: "#2E7D32",
                      "&.Mui-checked": { color: "#2E7D32" },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      color: "#2E7D32",
                    }}
                  >
                    {localize.approve}
                  </Typography>
                </CardActionArea>
              </Card>

              <Card
                variant="outlined"
                onClick={() =>
                  handleSupplyUsageStatusChange({
                    Id: AdditionalStatusCodeEnum.Rejected,
                  })
                }
                sx={{
                  flex: 1,
                  border:
                    supplyUsage?.Status === AdditionalStatusCodeEnum.Rejected
                      ? "2px solid #C62828"
                      : "1px solid #E0E0E0",
                  backgroundColor:
                    supplyUsage?.Status === AdditionalStatusCodeEnum.Rejected
                      ? "#FFEBEE"
                      : "transparent",
                  cursor: "pointer",
                  borderRadius: "8px",
                }}
              >
                <CardActionArea
                  sx={{
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <Radio
                    size="small"
                    checked={
                      supplyUsage?.Status === AdditionalStatusCodeEnum.Rejected
                    }
                    sx={{
                      color: "#C62828",
                      "&.Mui-checked": { color: "#C62828" },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      color: "#C62828",
                    }}
                  >
                    {localize.reject}
                  </Typography>
                </CardActionArea>
              </Card>
            </Box>

            {supplyUsage?.Status !== AdditionalStatusCodeEnum.InProgress && (
              <Alert
                severity={
                  supplyUsage?.Status === AdditionalStatusCodeEnum.Approved
                    ? "warning"
                    : "info"
                }
              >
                <label className="text-xs">
                  {supplyUsage?.Status === AdditionalStatusCodeEnum.Approved
                    ? localize.warning_output_inventory_movement
                    : localize.warning_output_inventory_movement_rejected}
                </label>
              </Alert>
            )}

            {supplyUsage?.Status === AdditionalStatusCodeEnum.Approved && (
              <TextField
                type="number"
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                value={supplyUsageQuantity}
                onChange={(e) => {
                  assingQuantity(e);
                }}
                label={localize.approved_quantity}
                size="small"
                id="Quantity"
                name="Quantity"
                variant="outlined"
                fullWidth
              />
            )}

            {supplyUsage?.Status != AdditionalStatusCodeEnum.InProgress && (
              <FormControl required fullWidth>
                <TextField
                  InputLabelProps={getInputLabelProps()}
                  inputProps={{
                    style: {
                      fontSize: "0.8em",
                    },
                    maxLength: maximumCharactersForSupplyUsageStatusObservation,
                  }}
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  id="outlined-basic"
                  name="Observation"
                  label={localize.observations}
                  variant="outlined"
                  onChange={handleSupplyUsageStatusObservationChange}
                  value={supplyUsage.StatusObservation}
                  helperText={
                    supplyUsage?.Status === AdditionalStatusCodeEnum.Rejected &&
                    supplyUsageStatusObservationCount === 0
                      ? localize.error_empty_field
                      : `${supplyUsageStatusObservationCount}/${maximumCharactersForSupplyUsageStatusObservation}`
                  }
                  error={
                    supplyUsage?.Status === AdditionalStatusCodeEnum.Rejected &&
                    supplyUsageStatusObservationCount === 0
                  }
                />
              </FormControl>
            )}
          </Box>
        </Box>
      }
    />
  );
}
